<template>
  <section
    class="platform-module-component"
    :class="instance">
    <div class="inner-container top-line-animation">
      <div
        class="horizontal-line-top"
        :class="topLineH"
        :style="`background-color: ${data.settings.line_color};`"></div>
      <div
        class="vertical-line-top"
        :class="topLineV"
        :style="`background-color: ${data.settings.line_color};`"></div>
      <div
        class="orb"
        :class="orb"
        :style="`background-color: ${data.settings.orb_color}; box-shadow: 0 0 20px ${data.settings.orb_color};`"></div>
    </div>
    <div
      class="inner-container title"
      v-html="data.title_area" />
    <div class="inner-container first-row">
      <div class="vertical-line"></div>
      <div class="orb"></div>
      <div class="inner-fence">
        <div
          class="left-col"
          v-html="data.first_row.text" />
        <div class="right-col">
          <img
            :src="data.first_row.image.url"
            :alt="data.first_row.image.alt" />
        </div>
      </div>
    </div>
    <div class="inner-container middle-line-animation">
      <div
        class="horizontal-line-top"
        :class="midLineH"
        :style="`background-color: ${data.settings.line_color};`"></div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "PlatformComponent",
  "CLASSNAME": "platform-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */
import gsap from 'gsap'
import useUtils from '@/composables/useUtils'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { onMounted, onBeforeUnmount } from 'vue'

defineProps(['data'])
// console.log(props.data)

gsap.registerPlugin(ScrollTrigger)
const { genUUID, device } = useUtils()

const topLineH = `top-line-h-${genUUID()}`
const topLineV = `top-line-v-${genUUID()}`
const orb = `orb-${genUUID()}`
const midLineH = `mid-line-h-${genUUID()}`
const instance = `platform-module-component-${genUUID()}`

const topLineAnimation = () => {
  gsap.fromTo(
    `.${topLineV}`,
    { scaleY: 0 },
    {
      scaleY: 1,
      scrollTrigger: {
        trigger: `.${instance}`,
        id: topLineV,
        start: 'top+=50 center',
        end: 'top+=200 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${topLineH}`,
    { scaleX: 0 },
    {
      scaleX: 1,
      scrollTrigger: {
        trigger: `.${instance}`,
        id: topLineH,
        start: 'top-=100 center',
        end: 'top+=50 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${orb}`,
    { scale: 0, opacity: 0, y: '-100%' },
    {
      scale: 1,
      opacity: 1,
      y: device.value === 'desktop' ? '150%' : '90%',
      scrollTrigger: {
        trigger: `.${instance}`,
        id: `so-${orb}`,
        start: 'top+=100 center',
        end: 'top+=200 center',
        scrub: true,
        markers: false,
      },
    }
  )
}

onMounted(() => {
  topLineAnimation()
})

onBeforeUnmount(() => {
  ScrollTrigger.getById(topLineH)?.kill()
  ScrollTrigger.getById(topLineV)?.kill()
  ScrollTrigger.getById(orb)?.kill()
})
</script>

<style lang="scss">
.platform-module-component {
  @apply mb-0;
  .inner-container.top-line-animation {
    @apply relative pb-10 my-0 lg:mt-8 lg:mb-20;
    .horizontal-line-top {
      @apply h-[1px] w-6/12 origin-left;
    }
    .vertical-line-top {
      @apply absolute h-10 lg:h-20 w-[1px] left-1/2 top-0 z-10 origin-top;
    }
    .orb {
      @apply absolute w-6 lg:w-8 h-6 lg:h-8 rounded-full left-[calc(50%-12px)] lg:left-[calc(50%-16px)] blur-[2px] top-2;
    }
  }
  .inner-container.title {
    @apply text-center flex flex-col items-center max-w-full mb-20 pt-16 lg:pt-0;
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      @apply heading-2 mb-8 leading-[45px] w-[60%] lg:w-full;
      strong {
        @apply heading-2 font-normal text-lm_yellow;
        sup {
          @apply font-nuances text-[12px] lg:text-[16px] -top-5 lg:-top-8 text-lm_yellow;
        }
      }
    }
    p {
      @apply large w-[90%] max-w-[800px];
      sup {
        @apply text-inherit text-xs -top-2.5;
      }
    }
  }
  .inner-container.first-row {
    @apply flex;
    .vertical-line {
      @apply absolute h-full lg:h-[calc(100%+5rem)] w-[1px] bg-lm_yellow;
    }
    .orb {
      @apply absolute w-4 h-4 rounded-full bg-lm_yellow -translate-x-2;
    }
    .inner-fence {
      @apply flex flex-col lg:flex-row pl-7 lg:pl-16 gap-x-[110px] gap-y-8 pb-12 lg:pb-0;
      .left-col {
        @apply w-full lg:w-[39%];
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          @apply text-[34px] leading-[38px] lg:leading-[50px] lg:text-[46px] heading-3 mb-8 relative -top-3 lg:-top-0;
        }
        p {
          @apply medium;
        }
      }
      .right-col {
        @apply w-full lg:w-[61%];
        img {
          @apply object-cover w-full h-full;
        }
      }
    }
  }
  .inner-container.middle-line-animation {
    @apply relative pb-0 mt-0 lg:mt-20 mb-0;
    .horizontal-line-top {
      @apply h-[1px] w-full lg:w-6/12 origin-left;
    }
  }
}
</style>
